import type { ChatMessage, EmojiReaction } from "@albertine/types/openapi/concierge-v1";
import type { InfiniteData } from "@tanstack/react-query";
import { compareDesc } from "date-fns/compareDesc";
import { parseISO } from "date-fns/parseISO";

export type ChatThreadPage = {
    messages: ChatMessage[];
    nextPageCursor: string | null;
    conversationId: string | null;
    unreadMessageIds: string[];
};

export type ChatThreadInfiniteData = InfiniteData<ChatThreadPage, string | undefined>;

export const EMPTY_CHAT_MESSAGES: ChatMessage[] = [];
export const EMPTY_UNREAD_MESSAGE_IDS: string[] = [];

export function mergeNewestFirst(
    current: ChatMessage[],
    incoming: ChatMessage[],
): ChatMessage[] {
    return [...incoming, ...current].toSorted((left, right) =>
        compareDesc(parseISO(left.sentAt), parseISO(right.sentAt)),
    );
}

export function upsertMessage(
    current: ChatMessage[],
    incoming: ChatMessage,
): ChatMessage[] {
    return mergeNewestFirst(
        current.filter((message) => message.id !== incoming.id),
        [incoming],
    );
}

export function replaceThreadMessages(
    page: ChatThreadPage | undefined,
    messages: ChatMessage[],
): ChatThreadPage {
    return {
        nextPageCursor: page?.nextPageCursor ?? null,
        conversationId: page?.conversationId ?? null,
        unreadMessageIds: page?.unreadMessageIds ?? EMPTY_UNREAD_MESSAGE_IDS,
        messages,
    };
}

export function mergeThreadMessages(
    page: ChatThreadPage | undefined,
    incoming: ChatMessage[],
): ChatThreadPage {
    return replaceThreadMessages(
        page,
        mergeNewestFirst(page?.messages ?? [], incoming),
    );
}

export function flattenChatThreadPages(
    data: ChatThreadInfiniteData | undefined,
): ChatThreadPage | undefined {
    if (!data?.pages.length) {
        return undefined;
    }

    const messagesById = new Map<string, ChatMessage>();
    for (const page of data.pages) {
        for (const message of page.messages) {
            messagesById.set(message.id, message);
        }
    }

    return {
        conversationId: data.pages[0]?.conversationId ?? null,
        unreadMessageIds: data.pages[0]?.unreadMessageIds ?? EMPTY_UNREAD_MESSAGE_IDS,
        nextPageCursor: data.pages.at(-1)?.nextPageCursor ?? null,
        messages: mergeNewestFirst([], [...messagesById.values()]),
    };
}

export function chatThreadPagesContainMessage(
    data: ChatThreadInfiniteData | undefined,
    messageId: string,
): boolean {
    return (
        data?.pages.some((page) =>
            page.messages.some((message) => message.id === messageId),
        ) ?? false
    );
}

export function updateChatThreadInfiniteData(
    data: ChatThreadInfiniteData | undefined,
    update: (page: ChatThreadPage | undefined) => ChatThreadPage,
): ChatThreadInfiniteData {
    return {
        pages: [update(flattenChatThreadPages(data))],
        pageParams: [undefined],
    };
}

export function upsertThreadMessage(
    page: ChatThreadPage | undefined,
    incoming: ChatMessage,
): ChatThreadPage {
    return replaceThreadMessages(page, upsertMessage(page?.messages ?? [], incoming));
}

export function removeThreadMessage(
    page: ChatThreadPage | undefined,
    messageId: string,
): ChatThreadPage {
    return replaceThreadMessages(
        page,
        (page?.messages ?? []).filter((message) => message.id !== messageId),
    );
}

export function setThreadMessageReaction(
    page: ChatThreadPage | undefined,
    messageId: string,
    memberReaction: EmojiReaction | null,
): ChatThreadPage {
    return replaceThreadMessages(
        page,
        (page?.messages ?? []).map((message) =>
            message.id === messageId ? { ...message, memberReaction } : message,
        ),
    );
}

export function getThreadMessageReaction(
    page: ChatThreadPage | undefined,
    messageId: string,
): EmojiReaction | null {
    return (
        page?.messages.find((message) => message.id === messageId)?.memberReaction ??
        null
    );
}
